import { ContractSuretyAccountUnderwritingModelSize, SuretyAccountId } from "@inrev/common";
import { useFetchSuretyAccount } from "@portal/api/account";
import { useUpdateModelSize } from "@portal/api/account/agent";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { DropdownInput } from "@portal/components/form/inputs/DropdownInput";
import { ConfirmationModal } from "@portal/components/layout/ConfirmationModal";
import { Card, Text } from "@portal/components/primitives";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { MutationErrorFlag } from "@portal/components/ui/MutationErrorFlag";
import { FC, useState } from "react";
import { exposureSizeOptions } from "../../../../../constants";

type AccountModelSizeSectionProps = {
	accountId: SuretyAccountId;
};

const getDisabledOptions = (
	currentModelSize: ContractSuretyAccountUnderwritingModelSize,
): ContractSuretyAccountUnderwritingModelSize[] => {
	if (currentModelSize === "large") return ["small", "medium"];
	if (currentModelSize === "medium") return ["small"];
	return [];
};

export const AccountModelSizeSection: FC<AccountModelSizeSectionProps> = ({ accountId }) => {
	const { suretyAccount, suretyAccountIsLoading, suretyAccountError, refetchSuretyAccount } =
		useFetchSuretyAccount(accountId);
	const [pendingModelSize, setPendingModelSize] = useState<
		ContractSuretyAccountUnderwritingModelSize | undefined
	>(undefined);

	const { updateModelSize, updateModelSizeIsLoading, updateModelSizeError } = useUpdateModelSize(
		accountId,
		"contract",
	);

	if (suretyAccountIsLoading) {
		return (
			<div className="h-[200px]">
				<CenteredSpinner />
			</div>
		);
	}

	if (suretyAccountError) {
		return (
			<ReportedRetryableLoadError
				error={suretyAccountError}
				resource="account"
				onRetry={refetchSuretyAccount}
			/>
		);
	}

	if (!suretyAccount) {
		return <CenteredSpinner />;
	}

	const currentModelSize = suretyAccount.modelSize;
	const disabledOptions = currentModelSize ? getDisabledOptions(currentModelSize) : [];
	const isClosed = suretyAccount.status === "closed";

	const handleModelSizeChange = (value: ContractSuretyAccountUnderwritingModelSize) => {
		if (value && value !== currentModelSize) {
			setPendingModelSize(value);
		}
	};

	const handleConfirmChange = () => {
		if (pendingModelSize) {
			updateModelSize(pendingModelSize);
			setPendingModelSize(undefined);
		}
	};

	const handleCancelChange = () => {
		setPendingModelSize(undefined);
	};

	const selectedLabel = exposureSizeOptions.find((opt) => opt.value === pendingModelSize)?.label;

	return (
		<>
			<Card elevation="raised" className="rounded-xl" data-testid="agent-account-bonding-need-card">
				<Card.Body className="flex flex-col space-y-5 p-6">
					<Text type="title" color="primary">
						12-Month Bonding Need
					</Text>
					{currentModelSize ? (
						<DropdownInput
							value={currentModelSize}
							options={exposureSizeOptions}
							onChange={handleModelSizeChange}
							placeholder="Select model size"
							disabled={isClosed || updateModelSizeIsLoading}
							disabledOptions={disabledOptions}
							className="w-full"
							buttonClassName="bg-surface-base h-[36px] text-[13.5px] font-[450]"
							anchor="bottom start"
						/>
					) : (
						<Text type="body" color="secondary">
							Not available
						</Text>
					)}
				</Card.Body>
			</Card>

			{pendingModelSize && (
				<ConfirmationModal
					message="Are you sure you want to make this change?"
					subMessage={`This will update the account's underwriting model size to be: ${selectedLabel}`}
					confirmButtonText="Confirm"
					cancelButtonText="Cancel"
					onConfirm={handleConfirmChange}
					onCancel={handleCancelChange}
				/>
			)}
			<MutationErrorFlag
				errorHeading="Update Model Size Error"
				mutationError={updateModelSizeError}
			/>
		</>
	);
};
